import type * as React from 'react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import {
	Drawer,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'
import { Button } from '../ui/button'

interface ResponsiveFormModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	title: string
	/** Extra class applied to <DialogContent> on desktop (e.g. "sm:max-w-lg") */
	dialogClassName?: string
	/** The id of the <form> element to submit when the confirm button is clicked */
	formId?: string
	confirmLabel?: string
	cancelLabel?: string
	onCancel?: () => void
	children: React.ReactNode
}

/**
 * Renders a Dialog on desktop (≥ 640 px) and a Drawer on mobile.
 * Handles the responsive breakpoint switch internally so pages don't
 * need to duplicate `useMediaQuery` + the Dialog/Drawer conditionals.
 */
export function ResponsiveFormModal({
	open,
	onOpenChange,
	title,
	dialogClassName,
	formId,
	confirmLabel,
	cancelLabel,
	onCancel,
	children,
}: ResponsiveFormModalProps) {
	const isDesktop = useMediaQuery('(min-width: 640px)')

	const hasFooter = confirmLabel || cancelLabel

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent
					className={cn(
						'flex max-h-[90dvh] flex-col sm:max-w-md',
						dialogClassName,
					)}
				>
					<DialogHeader>
						<DialogTitle>{title}</DialogTitle>
					</DialogHeader>
					<div className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto px-1">
						{children}
					</div>
					{hasFooter && (
						<div className="flex shrink-0 gap-3 pt-2">
							{cancelLabel && (
								<Button
									type="button"
									variant="cancel"
									onClick={onCancel}
									className="flex-1"
								>
									{cancelLabel}
								</Button>
							)}
							{confirmLabel && (
								<Button
									type="submit"
									form={formId}
									className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
								>
									{confirmLabel}
								</Button>
							)}
						</div>
					)}
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
			<DrawerContent>
				<DrawerHeader className="text-left">
					<DrawerTitle>{title}</DrawerTitle>
				</DrawerHeader>
				<div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-2">
					{children}
				</div>
				{hasFooter && (
					<DrawerFooter className="sticky bottom-0 bg-white border-t border-border pt-3 pb-4 flex-row gap-3">
						{cancelLabel && (
							<Button
								type="button"
								variant="cancel"
								onClick={onCancel}
								className="flex-1"
							>
								{cancelLabel}
							</Button>
						)}
						{confirmLabel && (
							<Button
								type="submit"
								form={formId}
								className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
							>
								{confirmLabel}
							</Button>
						)}
					</DrawerFooter>
				)}
			</DrawerContent>
		</Drawer>
	)
}
